import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    makeStyles,
    Typography,
} from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import React, { useState } from 'react';
import { useTranslate } from 'react-admin';
import BackofficeUrlQrCode from './BackofficeUrlQrCode';

const useStyles = makeStyles((theme) => ({
    infoButton: {
        color: '#006eb5',
        textTransform: 'none',
        fontWeight: 500,
    },
    infoIconButton: {
        color: '#5a6570',
    },
    section: {
        marginBottom: theme.spacing(2.5),
    },
    sectionTitle: {
        fontWeight: 600,
        color: '#092b4b',
        marginBottom: theme.spacing(1),
    },
    step: {
        color: '#495057',
        fontSize: '0.9rem',
        lineHeight: 1.5,
        marginBottom: theme.spacing(0.75),
    },
    cardInfoButton: {
        position: 'absolute',
        top: theme.spacing(1.5),
        right: theme.spacing(1.5),
        color: '#5a6570',
    },
    qrSection: {
        marginBottom: theme.spacing(2.5),
        paddingBottom: theme.spacing(2),
        borderBottom: '1px solid #dce3ea',
    },
    qrTitle: {
        fontWeight: 600,
        color: '#092b4b',
        marginBottom: theme.spacing(0.5),
        textAlign: 'center',
    },
    qrHint: {
        color: '#5a6570',
        fontSize: '0.875rem',
        textAlign: 'center',
        marginBottom: theme.spacing(1),
    },
}));

interface AddToHomeScreenInfoDialogProps {
    open: boolean;
    onClose: () => void;
}

export const AddToHomeScreenInfoDialog: React.FC<AddToHomeScreenInfoDialogProps> = ({ open, onClose }) => {
    const classes = useStyles();
    const translate = useTranslate();

    const androidSteps = [
        translate('app.pwa.android.step1'),
        translate('app.pwa.android.step2'),
        translate('app.pwa.android.step3'),
    ];

    const iosSteps = [
        translate('app.pwa.ios.step1'),
        translate('app.pwa.ios.step2'),
        translate('app.pwa.ios.step3'),
    ];

    return (
        <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
            <DialogTitle>{translate('app.pwa.title')}</DialogTitle>
            <DialogContent dividers>
                <Typography variant='body2' color='textSecondary' style={{ marginBottom: 16 }}>
                    {translate('app.pwa.intro')}
                </Typography>

                <Box className={classes.qrSection}>
                    <Typography className={classes.qrTitle}>{translate('app.pwa.qr.title')}</Typography>
                    <Typography className={classes.qrHint}>{translate('app.pwa.qr.hint')}</Typography>
                    <BackofficeUrlQrCode />
                </Box>

                <Box className={classes.section}>
                    <Typography className={classes.sectionTitle}>{translate('app.pwa.android.title')}</Typography>
                    {androidSteps.map((step, index) => (
                        <Typography key={`android-${index}`} className={classes.step}>
                            {index + 1}. {step}
                        </Typography>
                    ))}
                </Box>

                <Box className={classes.section}>
                    <Typography className={classes.sectionTitle}>{translate('app.pwa.ios.title')}</Typography>
                    {iosSteps.map((step, index) => (
                        <Typography key={`ios-${index}`} className={classes.step}>
                            {index + 1}. {step}
                        </Typography>
                    ))}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color='primary'>
                    {translate('app.pwa.close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

interface AddToHomeScreenInfoButtonProps {
    variant?: 'button' | 'icon' | 'outlined';
    fullWidth?: boolean;
}

export const AddToHomeScreenInfoButton: React.FC<AddToHomeScreenInfoButtonProps> = ({
    variant = 'button',
    fullWidth = false,
}) => {
    const classes = useStyles();
    const translate = useTranslate();
    const [open, setOpen] = useState(false);

    return (
        <>
            {variant === 'icon' && (
                <IconButton
                    className={classes.infoIconButton}
                    aria-label={translate('app.pwa.info')}
                    onClick={() => setOpen(true)}
                >
                    <InfoOutlinedIcon />
                </IconButton>
            )}

            {variant === 'button' && (
                <Button
                    className={classes.infoButton}
                    startIcon={<InfoOutlinedIcon />}
                    onClick={() => setOpen(true)}
                    fullWidth={fullWidth}
                >
                    {translate('app.pwa.info')}
                </Button>
            )}

            {variant === 'outlined' && (
                <Button
                    variant='outlined'
                    className={classes.infoButton}
                    startIcon={<InfoOutlinedIcon />}
                    onClick={() => setOpen(true)}
                    fullWidth={fullWidth}
                >
                    {translate('app.pwa.info')}
                </Button>
            )}

            <AddToHomeScreenInfoDialog open={open} onClose={() => setOpen(false)} />
        </>
    );
};

export const AddToHomeScreenInfoCardButton: React.FC = () => {
    const classes = useStyles();
    const translate = useTranslate();
    const [open, setOpen] = useState(false);

    return (
        <>
            <IconButton
                className={classes.cardInfoButton}
                aria-label={translate('app.pwa.info')}
                onClick={() => setOpen(true)}
                size='small'
            >
                <InfoOutlinedIcon />
            </IconButton>
            <AddToHomeScreenInfoDialog open={open} onClose={() => setOpen(false)} />
        </>
    );
};
